import { Document } from "@/graphql/__generated__/graphql";
import { useEffect, useState } from "react";
import mammoth from "mammoth";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { CircleLoader } from "../loaders";
import { cn } from "@/lib/utils";

function DocxRenderComponent({ selectedDocument, customHeightClassName }: { selectedDocument: Document, customHeightClassName?: string }) {
  const { t } = useTranslation(["translation", "common"]);
  const [docxContent, setDocxContent] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedDocument?.url) {
      setLoading(true);
      fetch(selectedDocument.url)
        .then(response => response.arrayBuffer()) // Get document as array buffer
        .then(buffer => {
          // Convert DOCX buffer to HTML using mammoth
          return mammoth.convertToHtml(
            { arrayBuffer: buffer },
            {
              ignoreEmptyParagraphs: false // Keep empty paragraphs in output
            }
          );
        })
        .then(result => {
          const content = result.value
            .replace(/<p><\/p>/g, "<p>&nbsp;</p>") // Replace empty paragraphs with non-breaking space
            .replace(/\n/g, "<br>"); // Convert newlines to HTML line breaks

          setDocxContent(content);
          setError(null);
        })
        .catch(err => {
          // Handle any errors during fetch/conversion
          setError(err instanceof Error ? err.message : t("edittender.documents.anerroroccurred"));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [selectedDocument]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <CircleLoader
          visible={true}
          height={80}
          width={80}
          strokeWidth={8}
          ariaLabel="oval-loading"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 flex flex-col gap-4">
        <div className="text-sm text-red-500 bg-red-50 p-4 rounded-md border border-red-200">
          <span className="font-medium mr-1">
            {t("edittender.documents.unabletoloaddocument")}
          </span>
        </div>
        <Button
          onClick={() => window.open(selectedDocument.url, "_blank")}
          variant="outline"
        >
          {t("common:buttons.download")}
        </Button>
      </div>
    );
  }

  return (
    <div
      className={cn("p-4 prose max-w-none overflow-y-auto", customHeightClassName ?? "max-h-[calc(100vh-150px)]")}
      dangerouslySetInnerHTML={{ __html: docxContent }}
    />
  );
}

export default DocxRenderComponent;
